import React, { useState } from 'react'
import store from 'store2'
import classNames from 'classnames'
import { post } from '~/utils/api'
import { FormattedMessage } from '~/translations/utils'
import LanguageContainer from '~/translations/LanguageContainer'
import trans from '~/translations/trans'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { RenderProps } from './common/Form'

type ApiResp = { error: Error } | string

export default ({}) => {
  const [locale, setLocale] = useState('')

  return (
    <LanguageContainer locale={locale} addToWindow={true}>
      <Inner setLocale={setLocale} />
    </LanguageContainer>
  )
}

type InnerProps = {
  setLocale: React.Dispatch<React.SetStateAction<string>>
}

const Inner: React.FC<InnerProps> = ({}) => {
  const [error, setError] = useState(false)

  const schema = z.object({
    username: z.string().min(1, { message: trans.valid.required() }),
    password: z.string().min(1, { message: trans.valid.required() }),
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  return (
    <div className="auth">
      <div>
        {error && (
          <div className="notification is-danger">
            <FormattedMessage id="auth / error" />
          </div>
        )}

        <form
          onSubmit={handleSubmit(async data => {
            setError(false)

            const resp = await post<ApiResp>('auth', data)

            if (typeof resp == 'object') {
              setError(true)
            } else {
              store.set('token', resp)
              window.location.href = '/'
            }
          })}
        >
          <Controller
            name="username"
            control={control}
            render={renderProps => (
              <TextField
                renderProps={renderProps}
                placeholder={intl.formatMessage({ id: 'auth / login' })}
                icon="fas fa-envelope"
                type="text"
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={renderProps => (
              <TextField
                renderProps={renderProps}
                placeholder={intl.formatMessage({ id: 'auth / login' })}
                icon="fas fa-lock"
                type="password"
              />
            )}
          />

          <div className="buttons is-centered">
            <button
              type="submit"
              className={classNames('button is-primary', {
                'is-loading': isSubmitting,
              })}
              disabled={isSubmitting}
            >
              <FormattedMessage id="auth / submit" />
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

type TextFieldProps = {
  renderProps: RenderProps
  type: string
  placeholder: string
  icon: string
}

const TextField: React.FC<TextFieldProps> = ({
  renderProps: { field, fieldState },
  type,
  placeholder,
  icon,
}) => {
  const error = fieldState.error?.message

  return (
    <div className="field">
      <p className="control has-icons-left">
        <input
          className={classNames('input', { 'is-danger': error })}
          type={type}
          placeholder={placeholder}
          {...field}
        />
        <span className="icon is-small is-left">
          <i className={icon} />
        </span>
      </p>

      {error && <p className="help is-danger">{error}</p>}
    </div>
  )
}
